// Plume puffs and ember / bomb sparks for Volcanoes.tsx (split out of
// volcanoShaders.ts). Same conventions: instanced ShaderMaterials, all motion
// a pure function of the shared `uTime`, per-instance data in `instanceColor`:
//   r = state level (0 dormant, 1 active, 2 erupting)
//   g = phase seed (per volcano), b = index fraction 0..1 (per particle)
// All per-puff variation (size, speed, drift, lifetime phase) is hashed from
// (b, g) in the vertex shader: no per-frame allocation, no extra draw calls.

import { VOLCANO_HASH } from './volcanoShaders.js';

/** Ash / steam plume puffs: view-space billboards rising and drifting. */
export const PLUME_VERT = /* glsl */ `
  ${VOLCANO_HASH}
  uniform float uTime;
  varying vec2 vUv;
  varying float vAge;
  varying float vActive;
  varying float vEr;
  varying float vSeed;
  void main() {
    vUv = uv;
    float lvl = instanceColor.r;
    vActive = min(lvl, 1.0);
    vEr = step(1.5, lvl);
    vSeed = instanceColor.g;
    float idx = instanceColor.b;
    // Per-puff randoms (stable per instance).
    float h1 = hash11(idx * 17.0 + vSeed * 5.0);
    float h2 = hash11(idx * 29.0 + vSeed * 3.0 + 1.7);
    float h3 = hash11(idx * 41.0 + vSeed * 11.0 + 4.2);
    // Speed jitter + lifetime phase jitter: puffs desynchronise for good.
    float speed = mix(0.07, 0.11, vActive) * (1.0 + 0.4 * vEr)
                * (0.8 + 0.4 * h3) * (0.85 + 0.3 * vSeed);
    float age = fract(uTime * speed + idx + h1 * 0.6);
    vAge = age;
    float rise = mix(0.8, 1.9, vActive) * mix(1.0, 1.9, vEr) * (0.85 + 0.3 * h2);
    // Dormant: only the first ~third of the puffs exist (thin steam).
    float live = vActive > 0.5 ? 1.0 : step(idx, 0.34);
    // Wind: one slowly turning heading per volcano, gusting per puff.
    float wa = 0.7 + 0.6 * sin(uTime * 0.05 + vSeed * 6.28);
    vec2 wind = vec2(cos(wa), sin(wa));
    float drift = age * age * (0.7 + 0.7 * h3) * mix(1.0, 1.4, vEr);
    float sway = sin(uTime * (0.35 + 0.4 * h2) + vSeed * 6.28 + age * 4.0 + h1 * 6.28)
               * 0.12 * age;
    // Erupting: a narrower, straighter column near the vent.
    float column = 1.0 - 0.7 * vEr * (1.0 - smoothstep(0.0, 0.45, age));
    vec2 spread = (vec2(h1, h2) - 0.5) * 0.4 * age * column;
    vec3 offs = vec3(
      (wind.x * drift + sway) * column + spread.x,
      age * rise,
      (wind.y * drift * 0.7 + sway * 0.6) * column + spread.y
    );
    float size = length(instanceMatrix[0].xyz) * mix(0.55, 0.8, vActive)
               * (0.5 + age * 2.3) * (0.72 + 0.56 * h1) * mix(1.0, 1.5, vEr);
    vec4 c = modelViewMatrix * vec4(instanceMatrix[3].xyz + offs, 1.0);
    c.xy += position.xy * size;
    gl_Position = live > 0.5 ? projectionMatrix * c : vec4(2.0, 2.0, 2.0, 1.0);
  }
`;

export const PLUME_FRAG = /* glsl */ `
  varying vec2 vUv;
  varying float vAge;
  varying float vActive;
  varying float vEr;
  varying float vSeed;
  void main() {
    vec2 d = vUv - 0.5;
    float r = length(d) * 2.0;
    float soft = smoothstep(1.0, 0.15, r);
    float lump = 0.8 + 0.2 * sin(atan(d.y, d.x) * 4.0 + vSeed * 20.0);
    float fade = smoothstep(0.0, 0.12, vAge) * smoothstep(1.0, 0.55, vAge);
    vec3 ash = mix(vec3(0.2, 0.18, 0.18), vec3(0.42, 0.4, 0.4), vAge);
    // Erupting ash is thick and dark.
    ash *= mix(1.0, 0.55, vEr);
    // Fresh puffs are lit from below by the lava.
    ash += vec3(0.5, 0.18, 0.03) * smoothstep(0.25, 0.0, vAge) * 0.7;
    vec3 steam = vec3(0.88, 0.9, 0.92);
    vec3 col = mix(steam, ash, vActive);
    // Erupting: a hot bright core column in the young puffs.
    float core = vEr * smoothstep(0.5, 0.0, vAge) * smoothstep(1.0, 0.1, r);
    col = mix(col, vec3(1.0, 0.62, 0.2), core * 0.9);
    float a = soft * soft * lump * fade * mix(0.22, 0.5, vActive) * (1.0 + 0.7 * vEr);
    gl_FragColor = vec4(col, min(a, 0.85));
  }
`;

/**
 * Embers (active) and lava bombs (erupting): tiny additive sparks flung from
 * the vent. Erupting ones follow a ballistic arc (launch up, fall back).
 */
export const EMBER_VERT = /* glsl */ `
  ${VOLCANO_HASH}
  uniform float uTime;
  varying vec2 vUv;
  varying float vFade;
  varying float vEr;
  void main() {
    vUv = uv;
    float lvl = instanceColor.r;
    float er = step(1.5, lvl);
    vEr = er;
    float seed = instanceColor.g;
    float idx = instanceColor.b;
    float age = fract(uTime * (0.35 + 0.25 * seed) * mix(1.0, 1.25, er)
                + idx + hash11(idx * 7.0 + seed) * 0.5);
    float ang = hash11(seed * 31.0 + idx * 7.0) * 6.28;
    float spread = (0.08 + 0.45 * hash11(seed * 13.0 + idx * 3.0)) * mix(1.0, 2.2, er);
    float launch = mix(0.95, 2.4, er) * (0.8 + 0.4 * hash11(idx * 5.0 + seed * 2.0));
    float grav = mix(0.55, 2.4, er);
    float y = max(age * launch - age * age * grav, 0.0);
    vec3 offs = vec3(cos(ang) * spread * age, y, sin(ang) * spread * age);
    vFade = mix(smoothstep(1.0, 0.45, age), smoothstep(1.0, 0.85, age), er)
          * smoothstep(0.0, 0.05, age);
    float size = length(instanceMatrix[0].xyz);
    vec4 c = modelViewMatrix * vec4(instanceMatrix[3].xyz + offs, 1.0);
    c.xy += position.xy * size;
    gl_Position = lvl > 0.5 ? projectionMatrix * c : vec4(2.0, 2.0, 2.0, 1.0);
  }
`;

export const EMBER_FRAG = /* glsl */ `
  varying vec2 vUv;
  varying float vFade;
  varying float vEr;
  void main() {
    float r = length(vUv - 0.5) * 2.0;
    float a = smoothstep(1.0, 0.0, r) * vFade;
    vec3 col = mix(vec3(1.0, 0.55, 0.15), vec3(1.0, 0.85, 0.45), vEr * (1.0 - r));
    gl_FragColor = vec4(col, a);
  }
`;
